import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Calendar, Clock, CheckCircle, AlertTriangle,
  Camera, ChevronRight, Wrench, Shield, AlertCircle, FileText
} from 'lucide-react';
import { getJobDescription } from '../../../../config/maintenanceJobDescriptions';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { getDefaultImage } from '../../../../utils/maintenanceImageHelper';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';
import MaintenanceCheckForm from './MaintenanceCheckForm';
import QuickAddEquipmentModal from './QuickAddEquipmentModal';
import PhotoAttachment from '../../../common/PhotoAttachment';
import { uuidv4 } from '../../../common/PhotoAttachment/pipeline/uuid';
import { useCrew } from '../../../../hooks/useCrew';
// FT-189: Centralized crew name resolution
import { resolveCrewName } from '../crew/utils/crewHelpers';
// FT-349c: draft persistence + orphan cleanup on Cancel confirm.
// FT-351: `VIEW_STATE_FORM_OPEN` / `VIEW_STATE_PHOTOS` — resume respects
// which sub-view the user was on when autosave last fired.
import {
  useMaintenanceDraft,
  DRAFT_STALE_MS,
  VIEW_STATE_FORM_OPEN,
  VIEW_STATE_PHOTOS,
} from '../../../../hooks/domain/maintenance/useMaintenanceDraft';
import { usePhotoStore } from '../../../common/PhotoAttachment/hooks/usePhotoStore';

const MaintenanceCheckViewMobile = ({
  job,
  onBack,
  onSave,
  equipment = [],
  onAddEquipment,
  maintenanceHistory = [],
  onCreateDefect,
  crewList = [],
  // FT-346: <PhotoAttachment> wiring (optional so tests without vessel context still render).
  // FT-349b: `onUpdateJobPhotos` retained for the FT-346 test harness only —
  // photos now live in local per-completion state.
  currentVesselId = null,
  capturedBy = 'unknown',
  onUpdateJobPhotos = null,
  canSignOff = false,
  onSignOff
}) => {
  const [signOffNow, setSignOffNow] = useState(false);
  // TODO: remove showCompleteForm (always true since FT-503 — form is now always visible inline)
  const [showCompleteForm, setShowCompleteForm] = useState(true);
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [equipmentImage, setEquipmentImage] = useState('');
  // Added state for collapsible sections
  const [showEquipment, setShowEquipment] = useState(false);
  const [showHistory, setShowHistory] = useState(true); // Default to true for history

  // FT-349b: per-completion state — completionId (stable id shared between
  // history record + job.completions[] entry) + photos (evidence for this
  // completion, threaded through in the onSave payload).
  const [completionId, setCompletionId] = useState(() => uuidv4());
  const [photos, setPhotos] = useState([]);

  // FT-349c: draft persistence + cancel-confirm modals.
  const { deleteByRecord } = usePhotoStore(currentVesselId);
  const {
    draft,
    loading: draftLoading,
    saveDraft,
    purgeDraft,
    discardDraft,
  } = useMaintenanceDraft(job?.id, currentVesselId, { deleteByRecord });
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [resumePromptHandled, setResumePromptHandled] = useState(false);

  // FT-191: Get crew data for name resolution in history display
  const { crew } = useCrew();

  // FT-189: Use centralized crew name resolution helper
  const getCrewDisplayName = (crewId) => {
    if (!crewId) return 'Not recorded';
    return resolveCrewName(crewId, crew || []);
  };
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    inspector: '',
    allUnitsChecked: true,
    defectsFound: false,
    notes: '',
    defectDetails: '',
    detailedMode: false,
    equipmentChecks: {}
  });

  // FT-144 FIX: Get job description - check BOTH job.description (FT-144 format) and job.jobDescription (legacy)
  // job.description = { purpose, scope } from FT-144 rewrite
  // job.jobDescription = legacy format
  // Also fall back to the separate lookup
  const rawDesc = job?.description || job?.jobDescription || getJobDescription(job?.equipmentType) || {};

  // FT-144 DEBUG: Log what we found
  console.log('[FT-144 MOBILE CheckView] job.description:', JSON.stringify(job?.description));
  console.log('[FT-144 MOBILE CheckView] job.jobDescription:', JSON.stringify(job?.jobDescription));
  console.log('[FT-144 MOBILE CheckView] resolved rawDesc:', JSON.stringify(rawDesc));

  // FT-147b FIX: Normalize job description - DON'T duplicate purpose to description
  // FT-144 format has { purpose, scope }, legacy has { description, purpose, scope, checklist }
  // If purpose exists, we render it separately - don't copy it to description
  const jobDescription = {
    ...rawDesc,
    // Only use description if it exists AND is different from purpose
    description: (rawDesc.description && rawDesc.description !== rawDesc.purpose) ? rawDesc.description : '',
    purpose: rawDesc.purpose || '',
    scope: rawDesc.scope || ''
  };

  // FT-78 FIX: Filter equipment by job.linkedEquipmentIds
  // Only show equipment that is actually linked to THIS job
  // If linkedEquipmentIds is empty/undefined, hide the equipment section entirely
  const linkedEquipment = useMemo(() => {
    const linkedIds = Array.isArray(job?.linkedEquipmentIds) ? job.linkedEquipmentIds : [];
    if (linkedIds.length === 0) return [];
    return equipment.filter(eq => linkedIds.includes(eq.id));
  }, [job?.linkedEquipmentIds, equipment]);

  // FT-305 hardening: normalize notes/requirement for safe display
  // - Arrays (from RA-domain generators) coerced to newline-joined strings
  // - Non-string values fall through to null (safely skipped by the conditional)
  const requirementText = Array.isArray(job?.requirement) ? job.requirement.join('\n') : job?.requirement;
  const notesText = Array.isArray(job?.notes) ? job.notes.join('\n') : job?.notes;

  // FT-349b: Photos are managed in local per-completion state (setPhotos).
  // We keep an `onUpdateJobPhotos`-aware wrapper so the FT-346 test harness
  // (which asserts the parent-callback route) still passes — in production,
  // MaintenanceModule no longer supplies onUpdateJobPhotos and photos live
  // purely in check-view state.
  const handlePhotosChange = useCallback((nextPhotos) => {
    setPhotos(nextPhotos);
    if (typeof onUpdateJobPhotos === 'function') {
      onUpdateJobPhotos(nextPhotos);
    }
  }, [onUpdateJobPhotos]);

  const canShowPhotos = Boolean(currentVesselId);

  // FT-349c: detect whether the user has any unsaved work.
  const hasContent = useMemo(() => {
    if (photos && photos.length > 0) return true;
    return Object.entries(formData || {}).some(([k, v]) => {
      if (k === 'date' || k === 'detailedMode' || k === 'equipmentChecks' ||
          k === 'allUnitsChecked' || k === 'defectsFound') return false;
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      return false;
    });
  }, [photos, formData]);

  const clearCompletionState = useCallback(() => {
    setShowCompleteForm(true);
    setSignOffNow(false);
    setPhotos([]);
    setCompletionId(uuidv4());
    setFormData({
      date: new Date().toISOString().split('T')[0],
      inspector: '',
      allUnitsChecked: true,
      defectsFound: false,
      notes: '',
      defectDetails: '',
      detailedMode: false,
      equipmentChecks: {}
    });
  }, []);

  // FT-349c: Cancel path. Show confirm modal when there's unsaved content so
  // we can cascade-delete photo blobs.
  // FT-503: when no content, also call onBack() so Cancel/Back navigates away
  // now that the form is always visible inline.
  const handleCancelCompletion = useCallback(() => {
    if (hasContent) {
      setShowCancelConfirm(true);
      return;
    }
    clearCompletionState();
    onBack();
  }, [hasContent, clearCompletionState, onBack]);

  const handleConfirmCancel = useCallback(async () => {
    await purgeDraft();
    clearCompletionState();
    setShowCancelConfirm(false);
    onBack();
  }, [purgeDraft, clearCompletionState, onBack]);

  // FT-356c: register the shell-level back handler. When there's unsaved content,
  // the handler shows a cancel-confirm modal; otherwise Back navigates away.
  // FT-503: form is always visible — guard on hasContent, not showCompleteForm.
  useSetBackHandler(hasContent ? handleCancelCompletion : onBack);

  // FT-349c: rehydrate prompt — non-stale draft + not yet handled this mount.
  useEffect(() => {
    if (draftLoading || resumePromptHandled) return;
    if (!draft || !draft.savedAt) return;
    if (Date.now() - draft.savedAt >= DRAFT_STALE_MS) return;
    setShowResumePrompt(true);
  }, [draft, draftLoading, resumePromptHandled]);

  const handleResumeDraft = useCallback(() => {
    if (!draft) return;
    if (draft.form) setFormData((prev) => ({ ...prev, ...draft.form }));
    if (Array.isArray(draft.photos)) setPhotos(draft.photos);
    if (draft.completionId) setCompletionId(draft.completionId);
    // FT-503: form is always visible inline — always true.
    setShowCompleteForm(true);
    setShowResumePrompt(false);
    setResumePromptHandled(true);
  }, [draft]);

  const handleDiscardDraft = useCallback(async () => {
    await purgeDraft();
    setCompletionId(uuidv4());
    setPhotos([]);
    setShowResumePrompt(false);
    setResumePromptHandled(true);
  }, [purgeDraft]);

  // FT-349c: autosave. Debounced inside the hook (500ms).
  // FT-351: also persist which sub-view the user is on so Resume restores it.
  useEffect(() => {
    if (draftLoading) return;
    if (!hasContent) return;
    const viewState = showCompleteForm ? VIEW_STATE_FORM_OPEN : VIEW_STATE_PHOTOS;
    saveDraft(formData, photos, completionId, viewState);
  }, [formData, photos, completionId, hasContent, draftLoading, saveDraft, showCompleteForm]);

  // Load equipment image
  React.useEffect(() => {
    if (job) {
      // Try to load saved image first
      const savedImage = localStorage.getItem(`equipment_image_${job.id}`);
      if (savedImage) {
        setEquipmentImage(savedImage);
      } else {
        // Use default image
        setEquipmentImage(getDefaultImage(job));
      }
    }
  }, [job]);

  if (!job) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.inspector) {
      alert('Please enter inspector name');
      return;
    }

    // FT-349b: include photos + completionId so the completion record owns its
    // evidence (see FT-349a — MaintenanceModule threads these through to
    // completeMaintenanceJob → addCompletedCheck).
    onSave({
      ...formData,
      jobId: job.id,
      completedAt: new Date().toISOString(),
      photos,             // FT-349b
      completionId,       // FT-349b
      signOffNow          // FT-397: optional immediate sign-off
    });

    // FT-349c: draft now redundant — delete key only, keep photo blobs since
    // they belong to the just-saved completion.
    discardDraft();

    // Reset
    setFormData({
      date: new Date().toISOString().split('T')[0],
      inspector: '',
      allUnitsChecked: true,
      defectsFound: false,
      notes: '',
      defectDetails: '',
      detailedMode: false,
      equipmentChecks: {}
    });

    setPhotos([]);
    setCompletionId(uuidv4());
  };

  // Get status info
  const getStatusInfo = () => {
    if (job.status === 'overdue') {
      return {
        color: 'text-red-600 bg-red-50',
        borderColor: 'border-red-200',
        icon: AlertTriangle,
        text: `${Math.abs(job.daysUntilDue)} days overdue`
      };
    }
    if (job.status === 'due_soon') {
      return {
        color: 'text-yellow-600 bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: Clock,
        text: `Due in ${job.daysUntilDue} days`
      };
    }
    return {
      color: 'text-green-600 bg-green-50',
      borderColor: 'border-green-200',
      icon: CheckCircle,
      text: 'Compliant'
    };
  };

  const status = getStatusInfo();
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-start gap-3">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            {/* Equipment thumbnail — display-only per FT-346. Upload path moved
                to the dedicated Evidence Photos section (PhotoAttachment). */}
            <div className="flex-shrink-0">
              <img
                src={equipmentImage}
                alt={getJobDisplayName(job)}
                className="w-14 h-14 object-cover rounded-lg shadow-sm"
              />
            </div>

            {/* Title, Status and Meta Info */}
            <div className="flex-1">
              {/* Title and Status row */}
              <div className="flex items-center justify-between mb-1">
                <h1 className="text-lg font-semibold text-gray-900 leading-tight">
                  {getJobDisplayName(job)}
                </h1>

                {/* Status badge */}
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${status.color} ${status.borderColor} border`}>
                  <StatusIcon className="inline h-3 w-3 mr-0.5" />
                  {job.status === 'overdue' ?
                    `${Math.abs(job.daysUntilDue)}d` :
                    job.status === 'due_soon' ?
                      `${job.daysUntilDue}d` :
                      '✓'
                  }
                </div>
              </div>

              {/* Meta info - under title, beside image */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {job.interval} {job.unit}
                </span>
                <span className="flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  {job.lastCompleted ?
                    new Date(job.lastCompleted).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short'
                    }) :
                    'Never'
                  }
                </span>
                {job.regulation && (
                  <span className="flex items-center text-blue-600">
                    <Shield className="h-3 w-3 mr-1" />
                    {job.regulation}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 space-y-4">

        {/* Job Description - Visible by default but compact */}
        {jobDescription && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Job Description</h3>
            {jobDescription.purpose && (
              <p className="text-sm text-gray-700 font-medium mb-2">
                {jobDescription.purpose}
              </p>
            )}
            {jobDescription.description && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {jobDescription.description}
              </p>
            )}
            {jobDescription.scope && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-700 mb-1">Scope:</p>
                <ul className="text-xs text-gray-600 space-y-0.5">
                  {(typeof jobDescription.scope === 'string' ? jobDescription.scope.split('\n') : jobDescription.scope)
                    .filter(line => line.trim())
                    .map((line, idx) => {
                      // Remove bullet point from the line if it starts with one
                      const cleanLine = line.trim().replace(/^[•·]\s*/, '');
                      return (
                        <li key={idx} className="flex items-start">
                          <span className="text-gray-400 mr-1">•</span>
                          <span>{cleanLine}</span>
                        </li>
                      );
                    })}
                </ul>
              </div>
            )}

            {/* Completion requirements */}
            {jobDescription.completion && (
              <div className="mt-3 p-2 bg-gray-50 rounded">
                <p className="text-xs font-medium text-gray-700 mb-1">Completion:</p>
                <p className="text-xs text-gray-600">{jobDescription.completion}</p>
              </div>
            )}

            {/* Include checklist items as part of description */}
            {jobDescription.checklist && jobDescription.checklist.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-700 mb-1">Check:</p>
                <ul className="text-xs text-gray-600 space-y-0.5">
                  {jobDescription.checklist.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-gray-400 mr-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* FT-305: Requirement - display-only inline (mobile) */}
            {typeof requirementText === 'string' && requirementText.trim() && (
              <div className="mt-3">
                <h4 className="text-xs font-medium text-gray-900 mb-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1.5 text-gray-600" />
                  Requirement
                </h4>
                <p className="text-xs text-gray-700 leading-relaxed break-words">{requirementText}</p>
              </div>
            )}

            {/* FT-305: Notes - display-only inline (mobile) */}
            {typeof notesText === 'string' && notesText.trim() && (
              <div className="mt-3">
                <h4 className="text-xs font-medium text-gray-900 mb-1 flex items-center">
                  <FileText className="h-3 w-3 mr-1.5 text-gray-600" />
                  Notes
                </h4>
                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words">{notesText}</p>
              </div>
            )}

            {jobDescription.estimatedTime && (
              <div className="flex items-center mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  <Clock className="inline h-3 w-3 mr-1" />
                  {jobDescription.estimatedTime}
                </span>
              </div>
            )}
          </div>
        )}

        {/* FT-346: Evidence Photos — new photo evidence pipeline (FT-340) */}
        {canShowPhotos && (
          <div
            className="bg-white rounded-lg border border-gray-200 p-4"
            data-testid="maintenance-photos-section"
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <Camera className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-medium text-slate-900">Evidence Photos</h3>
            </div>
            <p className="mb-3 text-[11px] text-slate-500">
              Capture photos as evidence of this maintenance check.
            </p>
            <PhotoAttachment
              recordType="maintenance_job"
              recordId={completionId}
              vesselId={currentVesselId}
              capturedBy={capturedBy}
              photos={photos}
              onChange={handlePhotosChange}
              maxCount={20}
              caption={true}
              emptyLabel="No photos yet. Add photos to record job evidence."
              testId="maintenance-job-photos"
            />
          </div>
        )}

        {/* FT-503: Inline Complete Check form — always visible, no toggle button. */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-1.5">
            <CheckCircle className="h-4 w-4 text-green-600" />
            Complete Check
          </h3>
          {/* FT-391: pb-24 md:pb-6 keeps the footer clear of the fixed
              MobileBottomBar (lg:hidden, ~65px, Back+Menu). */}
          <div className="pb-6">
            <MaintenanceCheckForm
              formData={formData}
              setFormData={setFormData}
              relevantEquipment={equipment.filter(e =>
                e.type?.toLowerCase().includes(job.equipmentType?.toLowerCase().split(' ')[0])
              )}
              onShowAddEquipment={() => setShowAddEquipment(true)}
              job={job}
              onCreateDefect={onCreateDefect}
              crewList={crewList}
            />

            {/* FT-391: Form Actions footer — matches sibling forms' Save/Cancel row. */}
            <div className="flex items-center justify-between mt-6">
              <div>
                {canSignOff && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={signOffNow}
                      onChange={e => setSignOffNow(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    Sign off now
                  </label>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCancelCompletion}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!formData.inspector}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-white bg-green-600 rounded-md disabled:opacity-50 min-h-[44px]"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Equipment List - FT-78 FIX: Only show LINKED equipment */}
        {linkedEquipment.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setShowEquipment(!showEquipment)}
              className="w-full px-4 py-3 flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-50"
            >
              <div className="flex items-center">
                <Wrench className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-sm font-medium">Equipment ({linkedEquipment.length})</span>
              </div>
              <ChevronRight
                className={`h-4 w-4 text-gray-400 transition-transform ${showEquipment ? 'rotate-90' : ''
                  }`}
              />
            </button>
            {showEquipment && (
              <div className="px-4 pb-3 border-t border-gray-100">
                {linkedEquipment.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-gray-600">{item.name || item.type}</span>
                    {item.serialNumber && (
                      <span className="text-xs text-gray-400">#{item.serialNumber}</span>
                    )}
                  </div>
                ))}
                {linkedEquipment.length > 5 && (
                  <p className="text-xs text-gray-500 mt-1">
                    +{linkedEquipment.length - 5} more items
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recent History */}
        {maintenanceHistory.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="w-full px-4 py-3 flex items-center justify-between cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-50"
            >
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-sm font-medium">Recent Checks</span>
              </div>
              <ChevronRight
                className={`h-4 w-4 text-gray-400 transition-transform ${showHistory ? 'rotate-90' : ''
                  }`}
              />
            </button>
            {showHistory && (
              <div className="px-4 pb-3 border-t border-gray-100">
                {/* FT-190: Filter by jobId NOT equipmentType to prevent phantom history */}
                {maintenanceHistory
                  .filter(h => h.jobId === job.id)
                  .slice(0, 3)
                  .map((record, idx) => (
                    <div key={idx} className="py-1.5 text-sm flex justify-between">
                      <span className="text-gray-600">
                        {new Date(record.date || record.completedAt).toLocaleDateString('en-GB')}
                      </span>
                      {/* FT-421: Use stored performerName first (FT-221 field), fall back to
                           crew-list lookup, then 'Not recorded'. Belt-and-braces: covers both
                           old records (inspector ID only) and new records (performerName set). */}
                      <span className="text-xs text-gray-500">{record.performerName || getCrewDisplayName(record.inspector) || 'Not recorded'}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddEquipment && (
        <QuickAddEquipmentModal
          isOpen={showAddEquipment}
          onClose={() => setShowAddEquipment(false)}
          onSave={onAddEquipment}
        />
      )}

      {/* FT-349c: Resume-draft prompt */}
      {showResumePrompt && draft && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          data-testid="maintenance-draft-resume-modal"
        >
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Resume draft check?</h3>
            <p className="text-sm text-gray-600 mb-5">
              You have a check in progress from{' '}
              {new Date(draft.savedAt).toLocaleString('en-GB')}. Resume or discard?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDiscardDraft}
                className="px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleResumeDraft}
                className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md"
              >
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FT-349c: Cancel-with-content confirm */}
      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          data-testid="maintenance-cancel-confirm-modal"
        >
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Discard unsaved work?</h3>
            <p className="text-sm text-gray-600 mb-5">
              You have unsaved photos and form data. Discard the check and cascade-delete the photos?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceCheckViewMobile;